<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '32rem' }"
    :breakpoints="{ '960px': '92vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div
          class="crc-form-dialog-header__icon"
          :style="{ backgroundColor: normalizedTagColor + '22', color: normalizedTagColor }"
        >
          <i class="pi pi-users text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ modalTitle }}
          </div>
        </div>
        <Tag
          :value="draft.name?.trim() ? draft.name : 'Preview'"
          :style="{ backgroundColor: normalizedTagColor, color: '#fff' }"
          class="!border-0 !px-3 !py-1 rounded-lg text-xs font-semibold crc-form-dialog-header__badge"
        />
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section crc-form-dialog-fields">
        <FloatLabel variant="on" class="w-full">
          <InputText
            id="team-edit-name"
            v-model="draft.name"
            :disabled="saving"
            maxlength="160"
            autocomplete="off"
            :invalid="submitted && !draft.name?.trim()"
            class="w-full rounded-md"
          />
          <label for="team-edit-name" class="text-sm font-medium">
            Name <span class="text-red-500">*</span>
          </label>
        </FloatLabel>
        <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1 -mt-2">
          Please enter a team name.
        </small>

        <QuickAddSelectField
          v-model="draft.department_id"
          input-id="team-edit-department"
          label="Department"
          :options="departmentOptions"
          show-clear
          :disabled="saving || !canSetDepartment"
          :can-add="canAddDepartment && canSetDepartment"
          add-label="Create New Department"
          create-placeholder="New department name"
          :max-length="120"
          :create-option="createDepartmentOption"
          @update:model-value="onDepartmentChange"
        />

        <QuickAddSelectField
          v-model="draft.sub_department_id"
          input-id="team-edit-subdept"
          label="Sub-department"
          :options="subDepartmentOptions"
          show-clear
          :disabled="saving || !draft.department_id"
          :can-add="canAddSubDepartment"
          :add-disabled="!draft.department_id"
          add-disabled-message="Select a department first"
          add-label="Create New Sub-department"
          create-placeholder="New sub-department name"
          :max-length="160"
          :create-option="createSubDepartmentOption"
        />

        <QuickAddSelectField
          v-model="draft.location_id"
          input-id="team-edit-location"
          label="Location"
          :options="locationOptions"
          show-clear
          :disabled="saving"
          :can-add="canAddLocation"
          add-label="Create New Location"
          create-placeholder="New location name"
          :max-length="160"
          :create-option="createLocationOption"
        />

        <FloatLabel variant="on" class="w-full">
          <Select
            inputId="team-edit-lead"
            v-model="draft.lead_employee_id"
            :options="leadOptions"
            optionLabel="label"
            optionValue="employee_id"
            showClear
            filter
            :disabled="saving"
            class="w-full"
            inputClass="w-full rounded-md"
          />
          <label for="team-edit-lead" class="text-sm font-medium">Team lead</label>
        </FloatLabel>
      </section>

      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-section__label">
          Appearance
        </div>
        <HexColorField
          ref="tagColorFieldRef"
          v-model="draft.tag_color"
          input-id="team-edit-color"
          fallback-color="#22C55E"
          :disabled="saving"
          :invalid="submitted"
          mono
          invalid-message="Enter a valid HEX color (e.g. #22C55E)."
          @change="tagColorLoaded = true"
        />
      </section>

      <section
        v-if="mode === 'edit'"
        class="crc-form-dialog-section"
      >
        <div class="crc-form-dialog-status">
          <div>
            <div class="crc-form-dialog-status__title">Active</div>
            <div class="crc-form-dialog-status__hint">
              Inactive teams are hidden from profile options.
            </div>
          </div>
          <ToggleSwitch v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
        <Button
          type="button"
          label="Cancel"
          severity="secondary"
          text
          :disabled="saving"
          @click="visibleProxy = false"
        />
        <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import Tag from 'primevue/tag';
import HexColorField from '@/components/common/HexColorField.vue';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';
import QuickAddSelectField from '@/components/forms/QuickAddSelectField.vue';
import { isValidHexWithHash, normalizeHexWithHash } from '@/utils/hexColor';
import { useAuth } from '@/composables/useAuth';
import { useQuickAddReference } from '@/features/org-references/composables/useQuickAddReference';
import { buildQuickAddPolicy } from '@/features/org-references/config/referencePermissions';
import { normalizeReferenceId } from '@/features/org-references/utils/referenceOptionUtils';

defineOptions({ name: 'TeamEditModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  departments: {
    type: Array,
    default: () => [],
  },
  subDepartments: {
    type: Array,
    default: () => [],
  },
  locations: {
    type: Array,
    default: () => [],
  },
  managers: {
    type: Array,
    default: () => [],
  },
  canSetDepartment: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:visible', 'save']);

const mode = ref('new');
const submitted = ref(false);
const tagColorLoaded = ref(false);
const tagColorFieldRef = ref(null);

const draft = reactive({
  id: null,
  name: '',
  department_id: null,
  sub_department_id: null,
  location_id: null,
  lead_employee_id: null,
  tag_color: '#22C55E',
  is_active: true,
});

const { isSuperAdmin, hasPermission } = useAuth();
const quickAddPolicy = computed(() =>
  buildQuickAddPolicy({ hasPermission, isSuperAdmin: isSuperAdmin.value })
);
const canAddDepartment = computed(() => quickAddPolicy.value.department);
const canAddSubDepartment = computed(() => quickAddPolicy.value.subDepartment);
const canAddLocation = computed(() => quickAddPolicy.value.location);

const departmentQuickAdd = useQuickAddReference({
  entity: 'department',
  options: computed(() => props.departments),
  onCreated(option) {
    draft.department_id = normalizeReferenceId(option.id);
    onDepartmentChange();
  },
});
const subDepartmentQuickAdd = useQuickAddReference({
  entity: 'subDepartment',
  context: computed(() => ({ departmentId: draft.department_id })),
  options: computed(() => props.subDepartments),
  onCreated(option) {
    draft.sub_department_id = normalizeReferenceId(option.id);
  },
});
const locationQuickAdd = useQuickAddReference({
  entity: 'location',
  context: computed(() => ({ locationType: 'office' })),
  options: computed(() => props.locations),
  onCreated(option) {
    draft.location_id = normalizeReferenceId(option.id);
  },
});

const createDepartmentOption = departmentQuickAdd.create;
const createSubDepartmentOption = subDepartmentQuickAdd.create;
const createLocationOption = locationQuickAdd.create;

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const normalizedTagColor = computed(() =>
  normalizeHexWithHash(draft.tag_color, '#22C55E')
);

const departmentOptions = computed(() =>
  Array.isArray(props.departments) ? props.departments : []
);

const subDepartmentOptions = computed(() => {
  if (!draft.department_id) return [];
  const deptId = Number(draft.department_id);
  return (props.subDepartments || []).filter(
    (item) =>
      item.department_id == null || Number(item.department_id) === deptId
  );
});

const locationOptions = computed(() =>
  Array.isArray(props.locations) ? props.locations : []
);

const leadOptions = computed(() => {
  const list = Array.isArray(props.managers) ? props.managers : [];
  if (!draft.department_id) return list;
  return list.filter(
    (item) => item.department_id == null || item.department_id === draft.department_id
  );
});

const modalTitle = computed(() => (mode.value === 'new' ? 'New Team' : 'Edit Team'));

const resetDraft = () => {
  draft.id = null;
  draft.name = '';
  draft.department_id = null;
  draft.sub_department_id = null;
  draft.location_id = null;
  draft.lead_employee_id = null;
  draft.tag_color = '#22C55E';
  draft.is_active = true;
  submitted.value = false;
  mode.value = 'new';
  tagColorLoaded.value = false;
  tagColorFieldRef.value?.resetTouched?.();
};

const fillDraft = (item) => {
  draft.id = item?.id ?? null;
  draft.name = item?.name || '';
  draft.department_id = item?.department_id ?? null;
  draft.sub_department_id = item?.sub_department_id ?? null;
  draft.location_id = item?.location_id ?? null;
  draft.lead_employee_id = item?.lead_employee_id ?? null;
  draft.tag_color = item?.tag_color || '#22C55E';
  draft.is_active = item?.is_active !== false;
  submitted.value = false;
  tagColorLoaded.value = Object.prototype.hasOwnProperty.call(item || {}, 'tag_color');
  tagColorFieldRef.value?.resetTouched?.();
};

const onDepartmentChange = () => {
  if (
    draft.sub_department_id &&
    !subDepartmentOptions.value.some(
      (item) => Number(item.id) === Number(draft.sub_department_id)
    )
  ) {
    draft.sub_department_id = null;
  }
};

const handleSubmit = () => {
  submitted.value = true;
  if (!draft.name?.trim() || !isValidHexWithHash(normalizedTagColor.value)) return;

  const payload = {
    name: draft.name.trim(),
    department_id: draft.department_id ?? null,
    sub_department_id: draft.sub_department_id ?? null,
    location_id: draft.location_id ?? null,
    lead_employee_id: draft.lead_employee_id ?? null,
  };

  if (mode.value === 'new' || tagColorLoaded.value) {
    payload.tag_color = normalizedTagColor.value;
  }

  if (mode.value === 'edit' && draft.id) {
    payload.team_id = draft.id;
    payload.is_active = draft.is_active ? 1 : 0;
  }

  emit('save', { mode: mode.value, payload });
};

const onClose = () => {
  if (!props.saving) {
    resetDraft();
  }
};

defineExpose({
  openNew: (preset = {}) => {
    resetDraft();
    mode.value = 'new';
    if (preset?.department_id != null) {
      draft.department_id = normalizeReferenceId(preset.department_id);
    }
  },
  openEdit: (item) => {
    mode.value = 'edit';
    fillDraft(item);
  },
});
</script>
